import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Badge,
  Divider,
  Container,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Add,
  Edit,
  Delete,
  CalendarToday,
  AccessTime,
  AttachMoney,
  Person,
  Store,
  Close,
  Today,
  CheckCircle,
  HelpOutline,
} from '@mui/icons-material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { AuthContext } from '../context/AuthContext';
import PayrollNotes from './PayrollNotes'; // Import original component for mobile
import { API_URL } from '../utils/apiConfig';

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const PayrollNotesCalendar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userRole, employeeId } = useContext(AuthContext);
  
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [payrollNotes, setPayrollNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteForm, setNoteForm] = useState({
    employeeId: employeeId || '',
    subject: '',
    content: '',
    noteDate: dayjs()
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [draggedNote, setDraggedNote] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const [helpTooltipOpen, setHelpTooltipOpen] = useState(false);

  // Fetch payroll notes
  useEffect(() => {
    fetchPayrollNotes();
  }, [currentMonth]);

  const fetchPayrollNotes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/payroll_notes`;
      
      // For user role, fetch their specific notes
      if (userRole === 'user' && employeeId) {
        url = `${API_URL}/payroll_notes/employee/${employeeId}`;
        
        // Add date filter
        const startOfMonth = currentMonth.startOf('month').format('YYYY-MM-DD');
        const endOfMonth = currentMonth.endOf('month').format('YYYY-MM-DD');
        url += `?payPeriodStart=${startOfMonth}&payPeriodEnd=${endOfMonth}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch payroll notes');
      }
      
      const data = await response.json();
      console.log('Fetched payroll notes:', data);
      // Ensure data is an array
      setPayrollNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching payroll notes:', err);
      setError('Failed to load payroll notes');
    } finally {
      setLoading(false);
    }
  };

  // Get calendar days for current month
  const getCalendarDays = () => {
    const startOfMonth = currentMonth.startOf('month');
    const endOfMonth = currentMonth.endOf('month');
    const startDate = startOfMonth.startOf('week');
    const endDate = endOfMonth.endOf('week');
    
    const days = [];
    let currentDate = startDate;
    
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      days.push(currentDate);
      currentDate = currentDate.add(1, 'day');
    }
    
    return days;
  };

  // Group notes by date
  const getNotesByDate = (date) => {
    return payrollNotes.filter(note => {
      const noteDate = dayjs(note.noteDate);
      return noteDate.isValid() && noteDate.isSame(date, 'day');
    });
  };

  // Get week boundaries
  const getWeekBoundaries = (date) => {
    const startOfWeek = date.startOf('week');
    const endOfWeek = date.endOf('week');
    return { startOfWeek, endOfWeek };
  };

  // Get notes for a specific week
  const getWeekNotes = (weekStart) => {
    const weekEnd = weekStart.endOf('week');
    return payrollNotes.filter(note => {
      const noteDate = dayjs(note.noteDate);
      return noteDate.isValid() && noteDate.isSameOrAfter(weekStart) && noteDate.isSameOrBefore(weekEnd);
    });
  };

  // Calculate week totals
  const getWeekTotals = (weekStart) => {
    const weekNotes = getWeekNotes(weekStart);
    return { count: weekNotes.length };
  };

  // Handle month navigation
  const handlePreviousMonth = () => {
    setCurrentMonth(currentMonth.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const handleToday = () => {
    setCurrentMonth(dayjs());
  };

  // Handle drag and drop
  const handleDragStart = (e, note) => {
    e.stopPropagation();
    setDraggedNote(note);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, date) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDate(date.format('YYYY-MM-DD'));
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDate(null);
  };

  const handleDrop = async (e, targetDate) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverDate(null);
    
    if (!draggedNote || !targetDate) return;
    
    // Update the note's date
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payroll_notes/${draggedNote.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...draggedNote,
          noteDate: targetDate.format('YYYY-MM-DD')
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update note date');
      }
      
      setSuccess('Note moved successfully');
      setTimeout(() => setSuccess(''), 3000);
      fetchPayrollNotes();
    } catch (err) {
      console.error('Error moving note:', err);
      setError('Failed to move note');
      setTimeout(() => setError(''), 3000);
    }
    
    setDraggedNote(null);
  };

  // Handle dialog operations
  const handleOpenDialog = (date = null, note = null) => {
    if (note) {
      setEditMode(true);
      setEditingNote(note);
      setNoteForm({
        employeeId: note.employeeId || employeeId || '',
        subject: note.subject || '',
        content: note.content || '',
        noteDate: dayjs(note.noteDate)
      });
    } else {
      setEditMode(false);
      setEditingNote(null);
      setNoteForm({
        employeeId: employeeId || '',
        subject: '',
        content: '',
        noteDate: date || dayjs()
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingNote(null);
    setNoteForm({
      employeeId: employeeId || '',
      subject: '',
      content: '',
      noteDate: dayjs()
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = editMode 
        ? `${API_URL}/payroll_notes/${editingNote.id}`
        : `${API_URL}/payroll_notes`;
      
      const method = editMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...noteForm,
          noteDate: noteForm.noteDate.format('YYYY-MM-DD'),
          employeeId: noteForm.employeeId || employeeId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to save payroll note');
      }
      
      setSuccess(editMode ? 'Note updated successfully' : 'Note added successfully');
      handleCloseDialog();
      fetchPayrollNotes();
    } catch (err) {
      console.error('Error saving payroll note:', err);
      setError('Failed to save payroll note');
    }
  };

  // Handle delete
  const handleDelete = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/payroll_notes/${noteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete payroll note');
      }
      
      setSuccess('Note deleted successfully');
      fetchPayrollNotes();
    } catch (err) {
      console.error('Error deleting payroll note:', err);
      setError('Failed to delete payroll note');
    }
  };

  // Render week summary sidebar
  const renderWeekSummary = (weekStart) => {
    const weekNotes = getWeekNotes(weekStart);
    const payPeriod = getPayPeriod(weekStart);
    const weekEnd = weekStart.endOf('week');
    
    return (
      <Paper
        elevation={0}
        sx={{
          bgcolor: payPeriod.isEvenWeek ? '#276a78' : '#4a90e2',
          color: 'white',
          p: 1.5,
          height: 180,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: payPeriod.isEvenWeek ? '#1a4a52' : '#357abd',
            transform: 'translateX(4px)',
          }
        }}
        onClick={() => setSelectedWeek(weekStart)}
      >
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold', mb: 0.5 }}>
          Pay Week {payPeriod.weekNumber}
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, mb: 0.5 }}>
          {weekStart.format('MMM D')} - {weekEnd.format('MMM D')}
        </Typography>
        {weekNotes.length > 0 && (
          <Chip
            label={`${weekNotes.length} note${weekNotes.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontSize: '0.7rem',
              height: 20
            }}
          />
        )}
      </Paper>
    );
  };

  // Get pay period for a date (assuming Sunday-Saturday pay periods)
  const getPayPeriod = (date) => {
    const weekStart = date.startOf('week');
    const weekEnd = date.endOf('week');
    const weekNumber = date.week();
    const isEvenWeek = weekNumber % 2 === 0;
    
    return {
      start: weekStart,
      end: weekEnd,
      weekNumber,
      isEvenWeek
    };
  };

  // Render calendar day
  const renderCalendarDay = (date) => {
    const isCurrentMonth = date.month() === currentMonth.month();
    const isToday = date.isSame(dayjs(), 'day');
    const isWeekStart = date.day() === 0;
    const isSaturday = date.day() === 6;
    const dayNotes = getNotesByDate(date);
    const payPeriod = getPayPeriod(date);
    const isDragOver = dragOverDate === date.format('YYYY-MM-DD');
    
    // Different background colors for alternating pay periods
    const payPeriodBgColor = payPeriod.isEvenWeek 
      ? 'rgba(39, 106, 120, 0.03)' // Light teal for even weeks
      : 'rgba(255, 255, 255, 0)'; // White for odd weeks
    
    return (
      <Card
        key={date.format('YYYY-MM-DD')}
        sx={{
          height: 180,
          position: 'relative',
          cursor: 'pointer',
          opacity: isCurrentMonth ? 1 : 0.6,
          bgcolor: isDragOver ? 'action.selected' : isToday ? 'action.hover' : payPeriodBgColor,
          border: '1px solid',
          borderColor: isDragOver ? 'primary.main' : 'divider',
          borderLeftWidth: isWeekStart ? 3 : 1,
          borderLeftColor: isWeekStart ? '#276a78' : (isDragOver ? 'primary.main' : 'divider'),
          borderRightWidth: isSaturday ? 3 : 1,
          borderRightColor: isSaturday ? '#276a78' : (isDragOver ? 'primary.main' : 'divider'),
          borderTopWidth: isWeekStart || (date.date() <= 7 && date.day() < date.date()) ? 2 : 1,
          borderTopColor: isWeekStart || (date.date() <= 7 && date.day() < date.date()) ? '#276a78' : (isDragOver ? 'primary.main' : 'divider'),
          overflow: 'hidden',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: 2,
            bgcolor: 'action.hover',
          }
        }}
        onClick={() => handleOpenDialog(date)}
        onDragOver={(e) => handleDragOver(e, date)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, date)}
      >
        <CardContent sx={{ p: 1, height: '100%', position: 'relative', overflow: 'hidden' }}>
          {/* Date number */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: isToday ? 'bold' : 'normal',
              color: isToday ? 'primary.main' : 'text.primary',
              position: 'absolute',
              top: 8,
              left: 8
            }}
          >
            {date.date()}
          </Typography>
          
          {/* Note count badge */}
          {dayNotes.length > 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 24,
                height: 24,
                bgcolor: 'primary.main',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                borderBottomLeftRadius: 4,
                boxShadow: 1
              }}
            >
              {dayNotes.length}
            </Box>
          )}
          
          {/* Display notes */}
          <Box sx={{ 
            overflow: 'auto', 
            maxHeight: 'calc(100% - 30px)',
            mt: 3.5
          }}>
            {dayNotes.map((note) => (
              <Paper
                key={note.id}
                draggable={userRole !== 'user'}
                onDragStart={(e) => handleDragStart(e, note)}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenDialog(null, note);
                }}
                elevation={0}
                sx={{
                  fontSize: '0.7rem',
                  p: 0.5,
                  mb: 0.5,
                  bgcolor: note.isAcknowledged ? 'success.light' : '#f5f5f5',
                  border: '1px solid',
                  borderColor: note.isAcknowledged ? 'success.main' : 'divider',
                  borderRadius: 0.5,
                  cursor: userRole !== 'user' ? 'move' : 'pointer',
                  '&:hover': {
                    boxShadow: 1,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.2 }}>
                  {note.subject}
                </Typography>
                {note.employeeName && (
                  <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                    {note.employeeName}
                  </Typography>
                )}
              </Paper>
            ))}
          </Box>
        </CardContent>
      </Card>
    );
  };

  // If mobile, render the original PayrollNotes component
  if (isMobile) {
    return <PayrollNotes />;
  }

  const calendarDays = getCalendarDays();
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#276a78' }}>
                  <CalendarToday sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Payroll Notes Calendar
                </Typography>
                <Tooltip
                  open={helpTooltipOpen}
                  onClose={() => setHelpTooltipOpen(false)}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        About the Payroll Notes Calendar
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.5 }}>
                        This calendar helps track important payroll-related notes and reminders for each pay period. 
                        Use it to document time-off requests, schedule changes, payroll adjustments, and other important employee information.
                      </Typography>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          How to Use:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          • Click any date to add a note<br/>
                          • Click existing notes to view or edit<br/>
                          {userRole !== 'user' && '• Drag notes to move them to different dates<br/>'}
                          • Pay periods are highlighted with alternating colors
                        </Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                          Visual Indicators:
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                          • <Box component="span" sx={{ color: '#276a78', fontWeight: 600 }}>Teal borders</Box> = Pay week boundaries<br/>
                          • <Box component="span" sx={{ color: '#4a90e2', fontWeight: 600 }}>Blue sections</Box> = Odd pay weeks<br/>
                          • <Box component="span" sx={{ color: '#276a78', fontWeight: 600 }}>Teal sections</Box> = Even pay weeks<br/>
                          • Number badges show note count per day
                        </Typography>
                      </Box>
                      
                      {userRole === 'user' && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
                          As an employee, you can view notes but cannot add or edit them. Contact your manager for any updates.
                        </Typography>
                      )}
                    </Box>
                  }
                  placement="bottom-start"
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: 400,
                      bgcolor: 'white',
                      color: 'text.primary',
                      border: '1px solid rgba(39, 106, 120, 0.2)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => setHelpTooltipOpen(!helpTooltipOpen)}
                    sx={{ color: '#276a78' }}
                  >
                    <HelpOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              {userRole !== 'user' && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  sx={{ bgcolor: '#276a78' }}
                >
                  Add Note
                </Button>
              )}
            </Box>

            
            {/* Month Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handlePreviousMonth}>
                <ChevronLeft />
              </IconButton>
              
              <Typography variant="h5" sx={{ minWidth: 200, textAlign: 'center' }}>
                {currentMonth.format('MMMM YYYY')}
              </Typography>
              
              <IconButton onClick={handleNextMonth}>
                <ChevronRight />
              </IconButton>
              
              <Button
                size="small"
                startIcon={<Today />}
                onClick={handleToday}
                sx={{ ml: 'auto' }}
              >
                Today
              </Button>
            </Box>
          </Box>

          {/* Calendar Grid */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                {/* Headers */}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                  <Box sx={{ width: 160, flexShrink: 0 }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        textAlign: 'center',
                        p: 1
                      }}
                    >
                      Pay Periods
                    </Typography>
                  </Box>
                  <Grid container spacing={0.5} sx={{ flex: 1 }}>
                    {weekDays.map(day => (
                      <Grid item xs={12/7} key={day}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            textAlign: 'center', 
                            fontWeight: 'bold',
                            color: 'text.secondary' 
                          }}
                        >
                          {day}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                {/* Calendar Weeks */}
                {calendarDays.filter(date => date.day() === 0).map((weekStart, weekIndex) => {
                  const weekDays = [];
                  for (let i = 0; i < 7; i++) {
                    weekDays.push(weekStart.add(i, 'day'));
                  }
                  
                  return (
                    <Box key={weekStart.format('YYYY-MM-DD')} sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                      {/* Pay Period Info */}
                      <Box sx={{ width: 160, flexShrink: 0 }}>
                        {renderWeekSummary(weekStart)}
                      </Box>
                      
                      {/* Week Days */}
                      <Grid container spacing={0.5} sx={{ flex: 1 }}>
                        {weekDays.map(date => (
                          <Grid item xs={12/7} key={date.format('YYYY-MM-DD')}>
                            {renderCalendarDay(date)}
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Success/Error Messages */}
          {success && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
        </Paper>

        {/* Week Summary Dialog */}
        <Dialog
          open={!!selectedWeek}
          onClose={() => setSelectedWeek(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Week Summary - {selectedWeek?.format('MMM D, YYYY')}
            <IconButton
              onClick={() => setSelectedWeek(null)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedWeek && (
              <Box>
                {getWeekNotes(selectedWeek).map(note => (
                  <Paper key={note.id} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <Box>
                        <Typography variant="subtitle1">
                          {dayjs(note.noteDate).format('dddd, MMM D')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                          {note.subject}
                        </Typography>
                        {note.employeeName && (
                          <Typography variant="body2" color="text.secondary">
                            {note.employeeName}
                          </Typography>
                        )}
                        {note.content && (
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {note.content}
                          </Typography>
                        )}
                        {note.isAcknowledged && (
                          <Chip 
                            label={`Acknowledged`} 
                            size="small" 
                            color="success"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </Box>
                      {userRole !== 'user' && (
                        <Box>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog(null, note)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDelete(note.id)}
                            color="error"
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  </Paper>
                ))}
                
                {/* Week Totals */}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Week Summary</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {getWeekTotals(selectedWeek).count} note{getWeekTotals(selectedWeek).count !== 1 ? 's' : ''} this week
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Add/Edit Note Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editMode ? 'Edit Payroll Note' : 'Add Payroll Note'}
            <IconButton
              onClick={handleCloseDialog}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label="Date"
                value={noteForm.noteDate}
                onChange={(newValue) => setNoteForm({ ...noteForm, noteDate: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />
              
              <TextField
                label="Subject"
                value={noteForm.subject}
                onChange={(e) => setNoteForm({ ...noteForm, subject: e.target.value })}
                fullWidth
                required
              />
              
              <TextField
                label="Content"
                value={noteForm.content}
                onChange={(e) => setNoteForm({ ...noteForm, content: e.target.value })}
                fullWidth
                multiline
                rows={4}
                required
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#276a78' }}>
              {editMode ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
};

export default PayrollNotesCalendar;